// 81 §4 — THE DEVICE ID IS ONE WE ISSUE.
//
// §4, in full, because every sentence of it is a constraint on this file:
//
//   "[MEASURED] No browser exposes a hardware identifier; that is deliberate.
//    The only alternatives are FINGERPRINTING — a tracking technique with its
//    own legal weather — and an identifier of our own.
//
//    MINT ONE. Issued on first sight, stored in the browser, sent with every
//    authentication attempt, opaque and meaningless on its own. It survives
//    until someone clears their browser data, which is honest and is stated on
//    the screens that show it.
//
//    BUILD IT ONCE. D.8, trusted devices, needs exactly this mechanism. Do not
//    build a second one there."
//
// ── WHAT MAKES THIS NOT A FINGERPRINT, PRECISELY ────────────────────────────
//
// A fingerprint is DERIVED — from fonts, canvas rendering, screen geometry,
// timezone — so it identifies a browser that never agreed to be identified, it
// follows a person across sites that have never met, and it cannot be cleared
// because there is nothing to clear. §10's third line forbids it.
//
// This is the opposite on every one of those counts. It is 128 random bits with
// NO INPUT AT ALL: nothing about the machine, the person, the network or the
// session goes into it, so it cannot be correlated with anything outside this
// application and carries no information even to whoever reads it. It exists
// only in this origin's localStorage. Clearing browser data removes it, and the
// next visit mints a different one — which is exactly why the screens say a
// device shown here is "this browser, until you clear its data" rather than
// "your laptop".
//
// ── IT SURVIVES LOGOUT, AND THAT IS AN EXCEPTION THAT OWES A REASON ─────────
//
// `clearAppStorage` (src/api.ts) sweeps every `lx:` key on sign-out, because
// 05s found a firm's whole client table surviving a logout on a borrowed
// machine. `lx:device` is in `LOGOUT_KEEP` beside `lx:lang`, and the reason is
// that a device id wiped at sign-out is not a device id: every sign-in would
// arrive as a new device, the attempt log could never say "the same browser as
// last time", and D.8's trusted devices would have nothing to trust. What is
// kept is 128 bits that say nothing about anybody — the sweep exists to stop a
// borrowed machine holding the FIRM'S DATA, and this is not that.
//
// ── WHY IT IS A HEADER AND NOT A BODY FIELD ─────────────────────────────────
//
// The three requests that carry an authentication attempt do not agree on a
// body: `/auth/login` sends an address and a password, `/auth/second-step`
// sends a challenge and six digits, and `/auth-factors/totp/confirm` sends a
// ticket. A header reaches all three, and every future one, without three
// routes agreeing on a field name. `api.ts` attaches it to every request rather
// than to the auth ones, so a route that starts caring later needs no client
// change — and there is nothing to leak by sending it: it is this browser's own
// opaque number, going to the server that issued it.

/** The one key. `lx:` because it is this application's, and the census walks that prefix. */
export const DEVICE_KEY = "lx:device";

/** The header both this file and `server/src/lib/authAttempts.js` name. */
export const DEVICE_HEADER = "X-LX-Device";

/**
 * The shape the server accepts (`deviceIdOf`, authAttempts.js): 8–64 characters
 * of `[A-Za-z0-9_-]`. Anything else is dropped there rather than stored, so a
 * value that fails this test would silently record no device at all.
 */
const SHAPE = /^[A-Za-z0-9_-]{8,64}$/;

/** 128 bits, base64url, no padding — 22 characters inside the shape above. */
function mint(): string {
  const bytes = new Uint8Array(16);
  // `crypto.getRandomValues` rather than `crypto.randomUUID`: the latter is
  // unavailable outside a secure context in some browsers, and this must work
  // on the day somebody opens the app over plain http on a LAN. Both are CSPRNG;
  // only one of them is always there.
  (globalThis.crypto || ({} as Crypto)).getRandomValues?.(bytes);
  // If there is no crypto at all, the array is still all zeros — and a device
  // id of zeros would be the SAME id for every browser in that state, which is
  // worse than none. Detected below rather than assumed away.
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** 16 zero bytes, base64url — what `mint()` returns when there is no CSPRNG. */
const ALL_ZERO = "AAAAAAAAAAAAAAAAAAAAAA";

/**
 * This browser's id, minted on first sight.
 *
 * NULL IS A REAL ANSWER AND CALLERS MUST TREAT IT AS ONE: a browser with
 * storage disabled (private mode in some browsers, a locked-down profile) has
 * nowhere to keep one, and an attempt from it records no device. That is honest
 * — the alternative is minting a fresh id per request, which would fill the
 * column with numbers that look like devices and are noise.
 */
export function deviceId(): string | null {
  let existing: string | null = null;
  try {
    existing = localStorage.getItem(DEVICE_KEY);
  } catch {
    return null; // storage unavailable — see above
  }
  if (existing && SHAPE.test(existing) && existing !== ALL_ZERO) return existing;

  const fresh = mint();
  // No CSPRNG: every browser in that state would otherwise share one id, which
  // is a false claim about identity rather than a missing one.
  if (fresh === ALL_ZERO || !SHAPE.test(fresh)) return null;
  try {
    localStorage.setItem(DEVICE_KEY, fresh);
  } catch {
    return null;
  }
  return fresh;
}

/**
 * The header to attach, or nothing.
 *
 * Spread at the call site (`...deviceHeader()`), so a browser with no id sends
 * no header at all rather than an empty one the server would have to interpret.
 */
export function deviceHeader(): Record<string, string> {
  const id = deviceId();
  return id ? { [DEVICE_HEADER]: id } : {};
}

/**
 * Forget this browser's id and mint a new one on the next call.
 *
 * NOT WIRED TO ANY SCREEN IN THIS BRIEF, and it is here for one reason: §4 says
 * the id "survives until someone clears their browser data", and D.8 will need
 * a way to say "this is not my device any more" that does not require a person
 * to be told about browser settings. Exported so that arrives as a call rather
 * than as a second mechanism.
 */
export function forgetDevice(): void {
  try {
    localStorage.removeItem(DEVICE_KEY);
  } catch {
    /* nothing to forget if there is nowhere to forget it */
  }
}
